import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {OrderOptions} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {createContext, use, useEffect, useState, useSyncExternalStore} from 'react'
import {getScreenReaderMessage} from '../helpers/screen-reader-helpers'
import type {DashboardTypes} from '@github-ui/shared-dashboard-sidebar/types/dashboard-types'
import {getEmptyListMessages} from '@github-ui/shared-dashboard-lists/shared/helpers/blankslate-helpers'
import type {EmptyListState} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'
import {defaultSortingOptions} from '../constants/pulls-sorting'

export class ScreenReaderAnnouncementState {
  #announcedTarget?: {query: string; page: number}
  #pendingTarget?: {query: string; page: number}
  #pendingRevision = 0
  #listeners = new Set<() => void>()

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  getPendingRevision = () => this.#pendingRevision

  markNavigationPending(query: string, page: number) {
    this.#pendingTarget = {query, page}
    this.#pendingRevision += 1
    for (const listener of this.#listeners) listener()
  }

  suppressTarget(query: string, page: number) {
    this.#announcedTarget = {query, page}
    this.#pendingTarget = undefined
  }

  hasSettledTarget() {
    return this.#announcedTarget !== undefined
  }

  initializeSettledTarget(query: string, page: number) {
    if (!this.#announcedTarget) this.#announcedTarget = {query, page}
  }

  shouldAnnounce(query: string, page: number) {
    const target = {query, page}
    if (this.#pendingTarget) return this.#targetsMatch(this.#pendingTarget, target)

    return this.#announcedTarget !== undefined && !this.#targetsMatch(this.#announcedTarget, target)
  }

  markAnnounced(query: string, page: number) {
    const target = {query, page}
    if (this.#targetsMatch(this.#announcedTarget, target) && !this.#targetsMatch(this.#pendingTarget, target)) {
      return false
    }

    this.#announcedTarget = target
    if (this.#targetsMatch(this.#pendingTarget, target)) this.#pendingTarget = undefined
    return true
  }

  #targetsMatch(a: {query: string; page: number} | undefined, b: {query: string; page: number} | undefined): boolean {
    return a !== undefined && b !== undefined && a.query === b.query && a.page === b.page
  }
}

export const ScreenReaderAnnouncementStateContext = createContext<ScreenReaderAnnouncementState | null>(null)

export function useScreenReaderAnnouncementState() {
  const [state] = useState(() => new ScreenReaderAnnouncementState())
  return state
}

/**
 * Hook to manage screen reader announcements.
 * Announces search results and empty states to screen reader users.
 * @param dashboardType - The type of dashboard (e.g., issues, pull requests).
 * @param isFetching - Whether a fetch is currently in progress.
 * @param currentPage - The current page number.
 * @param fetchedQuery - The query string that was fetched.
 * @param defaultSortingKey - The default sorting key to use.
 * @param resultCount - The number of results returned from the search.
 * @param pageCount - The total number of pages available for the current query.
 * @param errorMessage - Optional error message to announce when the query is invalid or results in an error.
 * @param resultNoun - Optional singular/plural noun override for the result count.
 * @param sortingOptions - Optional sorting options used to resolve the sort label in the announcement.
 *   Pass the same options supplied to the visual dropdown (e.g. issue-specific options that include
 *   reaction sub-options) so the announced label matches what is displayed.
 * @param additionalMessage - Optional context appended to the result announcement.
 * @returns An object containing the emptyListState for display.
 */
export function useScreenReaderAnnouncements(
  dashboardType: DashboardTypes,
  isFetching: boolean,
  currentPage: number,
  fetchedQuery: string,
  defaultSortingKey: string,
  resultCount: number,
  pageCount: number,
  errorMessage?: string,
  resultNoun?: {singular: string; plural: string},
  sortingOptions: Record<string, OrderOptions> = defaultSortingOptions,
  additionalMessage?: string,
) {
  const emptyListState: EmptyListState = {
    dashboardType,
    filtered: !!fetchedQuery,
    currentPage,
    pageCount,
    errorMessage,
  }

  const emptyMessages = getEmptyListMessages(emptyListState)

  const resultMessage = getScreenReaderMessage(
    dashboardType,
    sortingOptions,
    defaultSortingKey,
    () => emptyMessages,
    isFetching,
    currentPage,
    fetchedQuery,
    resultCount,
    pageCount,
    resultNoun,
  )
  const message = resultMessage && additionalMessage ? `${resultMessage} ${additionalMessage}` : resultMessage

  const persistentState = use(ScreenReaderAnnouncementStateContext)
  const pendingRevision = useSyncExternalStore(
    persistentState?.subscribe ?? emptySubscribe,
    persistentState?.getPendingRevision ?? getInitialRevision,
    persistentState?.getPendingRevision ?? getInitialRevision,
  )

  useEffect(() => {
    if (persistentState || !message) return

    const debouncedAnnounce = debounce(announce, 300)
    debouncedAnnounce(message)

    return () => {
      debouncedAnnounce.cancel()
    }
  }, [message, persistentState])

  useEffect(() => {
    if (!persistentState || isFetching || !message) return

    const shouldAnnounce = persistentState.shouldAnnounce(fetchedQuery, currentPage)
    if (!persistentState.hasSettledTarget() && !shouldAnnounce) {
      persistentState.initializeSettledTarget(fetchedQuery, currentPage)
      return
    }
    if (!shouldAnnounce) return

    const debouncedAnnounce = debounce(() => {
      if (persistentState.markAnnounced(fetchedQuery, currentPage)) announce(message)
    }, 300)
    debouncedAnnounce()

    return () => {
      debouncedAnnounce.cancel()
    }
  }, [currentPage, fetchedQuery, isFetching, message, pendingRevision, persistentState])

  return {emptyListState}
}

const emptySubscribe = () => () => undefined
const getInitialRevision = () => 0
